import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Trash2, X } from "lucide-react";
import { OrderDetailsProvider, useOrderDetails } from "../../context/OrderDetailContext";
import { getOrderDetails } from "../../services/orders/orderAxios";
import { handleApi } from "../../utils/handleApi";
import { useAuth } from "../../context/AuthContext";
import { MastershopState, Order } from "../../pages/SalesPage/SalesPageInterface";
import { OrderFulfillmentAction, OrderTopBar } from "../../pages/OrderDetailsPage/components/OrderTopBar";
import { OrderProductsCard } from "../../pages/OrderDetailsPage/components/OrderProductsCard";
import { OrderIntegrationsCard } from "../../pages/OrderDetailsPage/components/OrderIntegrationsCard";
import { OrderPropertiesPanel } from "../../pages/OrderDetailsPage/components/OrderPropertiesPanel";
import { hasOrderIntegrations } from "../../pages/OrderDetailsPage/components/orderTotals";
import { ConfirmationInfo, getConfirmationStatuses } from "../../services/whatsapp/whatsappService";
import { decodeTokenService } from "../../services/decodeTokenService";
import { dictionary } from "../../utils/dictionary";

const SkeletonBlock = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse rounded-2xl bg-gray-200/80 ${className}`} />
);

const OrderDetailSkeleton = () => (
  <div className="space-y-4 p-4">
    <div className="flex items-center gap-3">
      <SkeletonBlock className="h-9 w-9 rounded-full" />
      <SkeletonBlock className="h-6 w-36" />
      <SkeletonBlock className="h-7 w-24 rounded-full" />
    </div>
    <SkeletonBlock className="h-56 w-full" />
    <SkeletonBlock className="h-40 w-full" />
    <SkeletonBlock className="h-72 w-full" />
  </div>
);

const OrderDetailDrawerContent = ({
  hashId,
  onClose,
  onDelete,
}: {
  hashId: string;
  onClose: () => void;
  onDelete: (order: Order) => void;
}) => {
  const { language, timezone } = useAuth();
  const {
    products,
    customer,
    shippingAddress,
    orderDetails,
    loading,
    error,
    setOrderDetailsData,
    setLoadingState,
    setErrorState,
  } = useOrderDetails();
  const dataLoadedRef = useRef(false);
  const [errorOrderDetails, setErrorOrderDetails] = useState<string>();
  const [confirmationInfo, setConfirmationInfo] = useState<ConfirmationInfo | null>(null);

  const customFields = customer?.custom_fields ?? [];
  const normalizeKey = (key: unknown) => String(key ?? "").trim().toLowerCase();
  const isZipKey = (key: unknown) =>
    ["zip", "zip code", "postal_code", "postal code", "codigo postal", "código postal"].includes(normalizeKey(key));
  const zipCodeField = customFields.find((field) => isZipKey(field?.key));
  const zipCode = zipCodeField?.value ? String(zipCodeField.value) : undefined;

  const refreshOrderDetails = useCallback(async () => {
    const { res, error: requestError } = await handleApi(getOrderDetails(hashId), false);

    if (!requestError && res) {
      if (res.order?.dropi_integration_error) {
        setErrorOrderDetails(res.dropi_integration_error || res.order.dropi_integration_error);
      }
      setOrderDetailsData(res);
      dataLoadedRef.current = true;
    }
  }, [hashId, setOrderDetailsData]);

  const fetchOrderDetails = useCallback(async () => {
    setLoadingState(true);
    setErrorState(null);
    setErrorOrderDetails(undefined);
    const { res, error: requestError } = await handleApi(getOrderDetails(hashId), false);

    if (requestError) {
      setErrorState("Failed to load order details");
      setLoadingState(false);
      return;
    }

    if (res) {
      if (res.order?.dropi_integration_error) {
        setErrorOrderDetails(res.dropi_integration_error || res.order.dropi_integration_error);
      }
      setOrderDetailsData(res);
      dataLoadedRef.current = true;
    }
    setLoadingState(false);
  }, [hashId, setErrorState, setLoadingState, setOrderDetailsData]);

  useEffect(() => {
    dataLoadedRef.current = false;
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  useEffect(() => {
    if (!orderDetails?.id) return;
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (!ownerId) return;
    const fetchConfirmation = async () => {
      const { res, error: requestError } = await handleApi(getConfirmationStatuses(ownerId, [orderDetails.id]), false);
      if (!requestError && res) {
        const info = res[String(orderDetails.id)];
        if (info) setConfirmationInfo(info);
      }
    };
    fetchConfirmation();
  }, [orderDetails?.id]);

  useEffect(() => {
    const isMastershopPending =
      orderDetails?.mastershop_state === MastershopState.PENDING_SYNC ||
      orderDetails?.mastershop_state === MastershopState.WAITING_PAYMENT;

    if (!isMastershopPending) return;

    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (attempts > 8) {
        window.clearInterval(intervalId);
        return;
      }
      refreshOrderDetails();
    }, 15000);

    return () => window.clearInterval(intervalId);
  }, [orderDetails?.id, orderDetails?.mastershop_state, refreshOrderDetails]);

  const handleAddressUpdated = () => {
    dataLoadedRef.current = false;
    refreshOrderDetails();
  };

  if (loading) return <OrderDetailSkeleton />;

  if (error) {
    return (
      <div className="p-6 text-sm text-gray-600">
        {dictionary["error-getting-order-details"]?.[language] || "No se pudo cargar la orden."}
      </div>
    );
  }

  if (!orderDetails) return null;

  const withIntegrations = hasOrderIntegrations(orderDetails);

  return (
    <div className="min-h-full bg-[#F5F6FA]">
      <OrderTopBar
        order={orderDetails}
        confirmationInfo={confirmationInfo}
        onBack={onClose}
        compact
        showFulfillmentAction={false}
      />

      <div className="flex flex-col gap-4 p-4">
        <OrderProductsCard
          order={orderDetails}
          products={products}
          className={`animate-order-fade-up ${withIntegrations ? "" : "flex-1"}`}
        />
        <OrderPropertiesPanel
          className="animate-order-fade-up"
          style={{ animationDelay: "80ms" }}
          order={orderDetails}
          customer={customer}
          products={products}
          shippingAddress={shippingAddress}
          zipCode={zipCode}
          confirmationId={confirmationInfo?.confirmation_id}
          aiEnabled={confirmationInfo?.ai_enabled}
          onAddressUpdated={handleAddressUpdated}
        />
        <OrderIntegrationsCard
          order={orderDetails}
          customer={customer}
          dropiError={errorOrderDetails}
          timezone={timezone}
          language={language}
          onShopifyRetryComplete={refreshOrderDetails}
          onMastershopRetryComplete={refreshOrderDetails}
          onDropiSynced={handleAddressUpdated}
          className="animate-order-fade-up"
          style={{ animationDelay: "140ms" }}
        />

        <div className="sticky bottom-0 -mx-4 mt-2 flex flex-col items-end gap-2 bg-gradient-to-t from-[#F5F6FA] via-[#F5F6FA]/95 to-transparent px-4 py-4">
          <OrderFulfillmentAction order={orderDetails} />
          <button
            type="button"
            onClick={() => onDelete(orderDetails)}
            className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-600 transition-colors hover:border-red-200 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
            {dictionary["delete-order"]?.[language] || "Eliminar orden"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const OrderDetailDrawer = ({
  hashId,
  onClose,
  onDelete,
}: {
  hashId: string | null;
  onClose: () => void;
  onDelete: (order: Order) => void;
}) => {
  useEffect(() => {
    if (!hashId) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hashId, onClose]);

  const drawer = (
    <AnimatePresence>
      {hashId ? (
        <motion.aside
          className="fixed bottom-0 right-0 top-0 z-[70] flex w-full max-w-[500px] flex-col overflow-hidden border-l border-gray-200 bg-[#F5F6FA] shadow-xl"
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 24 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 z-30 flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-500 shadow-sm transition-colors hover:text-gray-900"
            aria-label="Cerrar detalle"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <OrderDetailsProvider key={hashId} hashId={hashId}>
              <OrderDetailDrawerContent hashId={hashId} onClose={onClose} onDelete={onDelete} />
            </OrderDetailsProvider>
          </div>
        </motion.aside>
      ) : null}
    </AnimatePresence>
  );

  if (typeof document === "undefined") return drawer;

  return createPortal(drawer, document.body);
};
